import React, { useEffect, useRef } from 'react';
import { Input } from 'components/ui/input';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { format, isValid, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, X } from 'lucide-react';
import { getTourListStatus, isDelivered } from './fleetTrackingHelpers';
import { CircleSpinner } from 'react-spinners-kit';
import RangeDateSelectionHistory from 'app/pages/HistoryPage/datesRangeSelector';

// Sidebar component for displaying and filtering the list of tours
// Props:
// - filteredTours: array of tours to display
// - selectedTour: currently selected tour
// - search: search string
// - dateRange: date range filter
// - showDatePicker: whether to show the date picker
// - onSearchChange: callback for search input
// - onDateRangeChange: callback for date range change
// - onToggleDatePicker: callback to toggle date picker visibility
// - onTourSelect: callback when a tour is selected
// - vehicles: list of vehicles
export interface FleetTourSidebarProps {
  filteredTours: any[];
  selectedTour: any;
  search: string;
  dateRange: Array<{
    startDate: Date;
    endDate: Date;
    key: string;
  }>;
  showDatePicker: boolean;
  onSearchChange: (v: string) => void;
  onDateRangeChange: (
    v: Array<{
      startDate: Date;
      endDate: Date;
      key: string;
    }>,
  ) => void;
  onToggleDatePicker: () => void;
  onTourSelect: (id: number) => void;
  vehicles: any[];
  isLoadingTours?: boolean;
}

const FleetTourSidebar: React.FC<FleetTourSidebarProps> = ({
  filteredTours,
  selectedTour,
  search,
  dateRange,
  showDatePicker,
  onSearchChange,
  onDateRangeChange,
  onToggleDatePicker,
  onTourSelect,
  vehicles,
  isLoadingTours = false,
}) => {
  const datePickerRef = useRef<HTMLDivElement>(null);

  const sortedAndGroupedTours = React.useMemo(() => {
    const grouped = filteredTours.reduce((acc, tour) => {
      const date = format(parseISO(tour.date), 'yyyy-MM-dd');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(tour);
      return acc;
    }, {});

    const sortedDates = Object.keys(grouped).sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    );

    return sortedDates.map(date => ({
      date,
      tours: grouped[date],
    }));
  }, [filteredTours]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node) &&
        showDatePicker
      ) {
        onToggleDatePicker();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker, onToggleDatePicker]);

  const formatDateRange = () => {
    const start = format(dateRange[0].startDate, 'dd/MM/yyyy', { locale: fr });
    const end = format(dateRange[0].endDate, 'dd/MM/yyyy', { locale: fr });
    if (start === end) {
      return start;
    }
    return `${start} - ${end}`;
  };

  return (
    <div className="bg-white p-4 border rounded-lg shadow-sm flex flex-col h-full max-h-[calc(100vh-200px)]">
      <div className="flex flex-col space-y-2 mb-4">
        <Input
          placeholder="Rechercher tournée, chauffeur..."
          value={search}
          onChange={e => onSearchChange(e.target.value)}
        />
        <div className="relative" ref={datePickerRef}>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onToggleDatePicker}
              className="flex items-center space-x-2"
            >
              <Calendar className="h-4 w-4" />
              <span>{formatDateRange()}</span>
            </Button>
            {showDatePicker && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleDatePicker}
                className="p-1"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          {showDatePicker && (
            <div className="absolute z-100 top-full mt-2 border rounded-lg p-2 bg-white shadow-lg left-0">
              <RangeDateSelectionHistory
                range={dateRange}
                setRange={onDateRangeChange}
              />
            </div>
          )}
        </div>
      </div>
      <div className="overflow-y-auto flex-1 space-y-3 pr-1">
        {isLoadingTours && (
          <div className="flex flex-col items-center justify-center my-2">
            <CircleSpinner size={40} color="#4a5568" />

            <div className="flex items-center justify-center mt-2 mb-2">
              <span className="text-blue-500 text-sm">Chargement...</span>
            </div>
          </div>
        )}
        {!isLoadingTours && filteredTours.length === 0 && (
          <p className="text-gray-500">Aucune tournée active trouvée.</p>
        )}
        {sortedAndGroupedTours.map(({ date, tours }) => (
          <div key={date} className="relative">
            <div className="sticky top-0 bg-white py-2 z-10 -mx-4 px-4 border-b border-t">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                {format(parseISO(date), 'EEEE, d MMMM', { locale: fr })}
              </h3>
            </div>
            <div className="space-y-3 pt-3">
              {tours.map(tour => {
                const completedStops = tour.orders.filter(isDelivered).length;
                const totalStops = tour.orders.length;
                const progressPercent =
                  totalStops > 0 ? (completedStops / totalStops) * 100 : 0;
                const isSelected = selectedTour?.id === tour.id;
                const tourStatus = getTourListStatus(tour);

                return (
                  <div
                    key={tour.id}
                    onClick={() => onTourSelect(tour.id)}
                    className={`p-3 rounded-lg cursor-pointer border ${
                      isSelected
                        ? 'bg-blue-50 border-blue-500 shadow-md ring-2 ring-blue-500'
                        : 'bg-white hover:bg-gray-50 border-gray-200'
                    } transition-all duration-150 ease-in-out`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <div>
                        <h3 className="font-bold text-md text-gray-800">{`Tournée ${tour.id}`}</h3>
                        <span className="text-sm text-gray-400 font-normal">
                          {format(new Date(tour.date), 'EEEE d MMMM yyyy', {
                            locale: fr,
                          })}
                        </span>
                      </div>
                      <Badge
                        variant={tourStatus.variant}
                        className="capitalize text-xs"
                      >
                        {tourStatus.text}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Chauffeur: {tour.drivers[0]?.name || 'N/D'}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      Plaque:{' '}
                      {vehicles?.find(v => v.id === tour.vehicleId)
                        ?.licensePlate || 'N/D'}
                    </p>

                    {!tour.delivered && totalStops > 0 && (
                      <div>
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Progression</span>
                          <span>
                            {completedStops}/{totalStops} arrêts
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              tourStatus.variant === 'outline'
                                ? 'bg-yellow-400'
                                : 'bg-blue-500'
                            }`}
                            style={{ width: `${progressPercent}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {tour.delivered && totalStops > 0 && (
                      <p className="text-xs text-green-600">
                        Terminée: {completedStops}/{totalStops} arrêts
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FleetTourSidebar;
